import { AccredibleButtonComponentModule } from '@accredible-frontend-v2/new-components/button';
import { AccredibleResponsiveBreakpoint } from '@accredible-frontend-v2/utils/responsive-helper';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  input,
  signal,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { Swiper } from 'swiper';
import { SwiperOptions } from 'swiper/types';
import { AccredibleBaseCarouselComponent } from '../base-carousel/base-carousel.component';
import { AccredibleCarouselItemComponent } from '../carousel-item/carousel-item.component';

const SLIDES_OFFSET_AFTER_MOBILE = 15;
const SLIDES_OFFSET_AFTER_DESKTOP = 30;

@Component({
  standalone: true,
  selector: 'accredible-carousel-multi',
  imports: [
    CommonModule,
    TranslocoDirective,
    AccredibleBaseCarouselComponent,
    AccredibleCarouselItemComponent,
    AccredibleButtonComponentModule,
  ],
  templateUrl: './carousel-multi.component.html',
  styleUrls: ['./carousel-multi.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccredibleCarouselMultiComponent {
  title = input('');
  titleFontClass = input('font-xl-bold');
  subtitle = input('');
  hideControls = input(false);
  dataCy = input('');
  // The full list of options can be found here: https://swiperjs.com/swiper-api#parameters
  customOptions = input<SwiperOptions>();

  carouselItems = contentChildren(AccredibleCarouselItemComponent, { descendants: true });

  isBeginning = signal(true);
  isEnd = signal(true);
  swiper = signal<Swiper>(null);

  defaultOptions: SwiperOptions = {
    slidesPerView: 'auto',
    slidesOffsetAfter: SLIDES_OFFSET_AFTER_MOBILE,
    spaceBetween: 12,
    freeMode: true,
    breakpoints: {
      [AccredibleResponsiveBreakpoint.SM]: {
        slidesOffsetAfter: SLIDES_OFFSET_AFTER_DESKTOP,
      },
    },
    on: {
      init: (swiper: Swiper) => {
        this.swiper.set(swiper);
        this._updateSwiperStates(swiper);
      },
      reachBeginning: () => {
        this.isBeginning.set(true);
      },
      reachEnd: () => {
        this.isEnd.set(true);
      },
      slideChange: (swiper: Swiper) => {
        this._updateSwiperStates(swiper);
      },
      slidesLengthChange: (swiper: Swiper) => {
        this._updateSwiperStates(swiper);
      },
      transitionEnd: (swiper: Swiper) => {
        this._updateSwiperStates(swiper);
      },
    },
  };

  options = computed(() => ({
    ...this.defaultOptions,
    ...this.customOptions(),
  }));

  next(): void {
    this.swiper().slideNext();
  }

  previous(): void {
    this.swiper().slidePrev();
  }

  private _updateSwiperStates(swiper: Swiper): void {
    this.isBeginning.set(swiper.isBeginning);
    this.isEnd.set(swiper.isEnd);

    if (!this._swiperNeedsControls(swiper)) {
      this.isEnd.set(true);
    }
  }

  // Small hack to disable the controls when the carousel has few items
  private _swiperNeedsControls(swiper: Swiper): boolean {
    return swiper.snapGrid.length > 2 || swiper.snapGrid[1] > SLIDES_OFFSET_AFTER_DESKTOP;
  }
}
